import axios from 'axios'
import type { Action, ActionAttachment, ActionComment } from '@/config/types/Action'
import type { Plan } from '@/config/types/Plan'
import { mapActionFromApi, mapCommentFromApi, type ApiAttachment } from '@/utils/mappers'
import { formatShortDate, formatLongDate } from '@/utils/formatters'

/**
 * Action state type and utilities
 */
export type ActionState = 'pending' | 'active' | 'complete'

export interface StateOption {
  value: ActionState
  label: string
  icon: string
  color: string
  badgeClass: string
  severity: 'secondary' | 'info' | 'success'
}

const stateOptions: StateOption[] = [
  {
    value: 'pending',
    label: 'Open',
    icon: 'pi pi-clock',
    color: '#94a3b8',
    badgeClass: 'bg-gray-100 text-(--p-anthracite-500)',
    severity: 'secondary',
  },
  {
    value: 'active',
    label: 'Active',
    icon: 'pi pi-play',
    color: '#3b82f6',
    badgeClass: 'bg-blue-100 text-blue-700',
    severity: 'info',
  },
  {
    value: 'complete',
    label: 'Complete',
    icon: 'pi pi-check-circle',
    color: '#22c55e',
    badgeClass: 'bg-green-100 text-green-700',
    severity: 'success',
  },
]

export default function useActions() {
  const createAction = async (payload: Action & { planId: string | number }): Promise<Action> => {
    const { planId, ...a }: { planId: string | number } & Action = payload
    const body: {
      plan_id: number
      title: string
      due_date: string
      state: 'pending' | 'active' | 'complete'
      color_hex?: string | null
      kpi_area?: string | null
      details?: string | null
      is_private?: boolean
      retailer_statuses?: Array<{ retailer_id: number; state: 'pending' | 'active' | 'complete' }>
    } = {
      plan_id: Number(planId),
      title: a.title,
      due_date: a.dueDate,
      state: a.state,
      color_hex: a.colorHex,
      kpi_area: a.kpiArea,
      details: a.details,
      is_private: a.isPrivate ?? true,
    }

    if (a.retailerStatuses && a.retailerStatuses.length > 0) {
      body.retailer_statuses = a.retailerStatuses.map((rs) => ({
        retailer_id: Number(rs.retailerId),
        state: rs.state,
      }))
    }

    const { data } = await axios.post('/api/actions', body)
    return mapActionFromApi(data)
  }

  const updateAction = async (payload: Action): Promise<Action> => {
    const body: {
      title: string
      due_date: string
      state: 'pending' | 'active' | 'complete'
      color_hex?: string | null
      kpi_area?: string | null
      details?: string | null
      is_private?: boolean
      retailer_statuses?: Array<{ retailer_id: number; state: 'pending' | 'active' | 'complete' }>
    } = {
      title: payload.title,
      due_date: payload.dueDate,
      state: payload.state,
      color_hex: payload.colorHex,
      kpi_area: payload.kpiArea,
      details: payload.details,
      is_private: payload.isPrivate ?? false,
    }
    if (payload.retailerStatuses !== undefined) {
      body.retailer_statuses =
        payload.retailerStatuses.length > 0
          ? payload.retailerStatuses.map((rs) => ({
              retailer_id: Number(rs.retailerId),
              state: rs.state,
            }))
          : []
    }

    const { data } = await axios.put(`/api/actions/${payload.id}`, body)
    return mapActionFromApi(data)
  }

  const duplicateAction = async (actionId: string | number): Promise<Action> => {
    const { data } = await axios.post(`/api/actions/${actionId}/duplicate`)
    return mapActionFromApi(data)
  }

  const getActionRetailer = (action: Action, plans?: Plan[]): string | null => {
    if (!plans) return null
    const plan = plans.find((p) => p.actions.some((a) => a.id === action.id))
    if (!plan) return null

    if (plan.groups && plan.groups.length > 0) {
      if (plan.groups.length === 1) {
        return plan.groups[0].label
      }
      return `${plan.groups.length} Groups`
    }

    if (plan.retailers && plan.retailers.length > 0) {
      if (plan.retailers.length === 1) {
        return plan.retailers[0].label
      }
      return `${plan.retailers.length} Retailers`
    }

    return null
  }

  const getActionRetailers = (
    action: Action,
    plans?: Plan[],
  ): Array<{ label: string; code: string }> => {
    if (!plans) return []
    const plan = plans.find((p) => p.actions.some((a) => a.id === action.id))
    if (!plan || !plan.retailers || plan.retailers.length === 0) return []
    return plan.retailers.map((r) => ({ label: r.label, code: r.code }))
  }

  const getActionGroups = (
    action: Action,
    plans?: Plan[],
  ): Array<{ label: string; code: string }> => {
    if (!plans) return []
    const plan = plans.find((p) => p.actions.some((a) => a.id === action.id))
    if (!plan || !plan.groups || plan.groups.length === 0) return []
    return plan.groups.map((g) => ({ label: g.label, code: g.code }))
  }

  const getActionPlan = (action: Action, plans?: Plan[]): Plan | undefined => {
    if (!plans) return undefined
    return plans.find((p) => p.actions.some((a) => a.id === action.id))
  }

  // State utilities
  const getStateIcon = (state: ActionState): string => {
    return stateOptions.find((s) => s.value === state)?.icon || 'pi pi-circle'
  }

  const getStateColor = (state: ActionState): string => {
    return stateOptions.find((s) => s.value === state)?.color || '#94a3b8'
  }

  const getStateBadgeClass = (state: ActionState): string => {
    return stateOptions.find((s) => s.value === state)?.badgeClass || 'bg-gray-100 text-(--p-anthracite-500)'
  }

  const getStateSeverity = (state: ActionState): 'secondary' | 'info' | 'success' => {
    return stateOptions.find((s) => s.value === state)?.severity || 'secondary'
  }

  const getStateOption = (state: ActionState): StateOption | undefined => {
    return stateOptions.find((s) => s.value === state)
  }

  const getStateLabel = (state: ActionState): string => {
    return stateOptions.find((s) => s.value === state)?.label || state
  }

  const getAllStateOptions = (): StateOption[] => {
    return [...stateOptions]
  }

  // Date formatting utilities
  const formatActionDate = (dateString: string | Date): string => {
    const date = dateString instanceof Date ? dateString : new Date(dateString)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const actionDate = new Date(date)
    actionDate.setHours(0, 0, 0, 0)

    const diffTime = actionDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays === -1) return 'Yesterday'
    if (diffDays < 0) return `${Math.abs(diffDays)} days ago`
    if (diffDays <= 7) return `In ${diffDays} days`

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
    })
  }

  const formatShort = formatShortDate
  const formatLong = formatLongDate

  // Display utilities
  const getKpiAreaColorClass = (): string => {
    return 'bg-blue-50 text-blue-700'
  }

  const deleteAction = async (actionId: string | number): Promise<void> => {
    await axios.delete(`/api/actions/${actionId}`)
  }

  const restoreAction = async (actionId: string | number): Promise<Action> => {
    const { data } = await axios.post(`/api/actions/${actionId}/restore`)
    return mapActionFromApi(data)
  }

  const fetchTrashedActions = async (): Promise<Action[]> => {
    const { data } = await axios.get('/api/actions/trashed')
    return data.map(mapActionFromApi)
  }

  return {
    createAction,
    updateAction,
    duplicateAction,
    deleteAction,
    restoreAction,
    fetchTrashedActions,
    getActionRetailer,
    getActionRetailers,
    getActionGroups,
    getActionPlan,
    // State utilities
    getStateIcon,
    getStateColor,
    getStateBadgeClass,
    getStateSeverity,
    getStateOption,
    getStateLabel,
    getAllStateOptions,
    stateOptions,
    // Date formatting utilities
    formatActionDate,
    formatShort,
    formatLong,
    // Display utilities
    getKpiAreaColorClass,
  }
}

export async function uploadActionAttachment(actionId: string | number, file: File) {
  const form = new FormData()
  form.append('file', file)
  const { data } = await axios.post(`/api/actions/${actionId}/attachments`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return {
    id: data.id,
    originalName: data.original_name,
    storedPath: data.stored_path,
    sizeBytes: data.size_bytes,
    mimeType: data.mime_type || undefined,
  }
}

export async function downloadActionAttachment(
  actionId: string | number,
  attachmentId: string | number,
  fileName: string,
): Promise<void> {
  const response = await axios.get(`/api/actions/${actionId}/attachments/${attachmentId}`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export async function deleteActionAttachment(
  actionId: string | number,
  attachmentId: string | number,
) {
  await axios.delete(`/api/actions/${actionId}/attachments/${attachmentId}`)
}

export async function fetchActionAttachments(
  actionId: string | number,
): Promise<ActionAttachment[]> {
  const { data } = await axios.get<ApiAttachment[]>(`/api/actions/${actionId}/attachments`)
  return data.map((att: ApiAttachment) => ({
    id: att.id,
    originalName: att.original_name,
    storedPath: att.stored_path,
    sizeBytes: att.size_bytes,
    mimeType: att.mime_type || undefined,
  }))
}

export async function fetchActionComments(actionId: string | number): Promise<ActionComment[]> {
  const { data } = await axios.get(`/api/actions/${actionId}/comments`)
  return data.map(mapCommentFromApi)
}

export async function postActionComment(
  actionId: string | number,
  body: string,
  parentId?: string | number | null,
): Promise<ActionComment> {
  const { data } = await axios.post(`/api/actions/${actionId}/comments`, {
    body,
    parent_id: parentId ?? null,
  })
  return mapCommentFromApi(data)
}

export async function deleteActionComment(actionId: string | number, commentId: string | number) {
  await axios.delete(`/api/actions/${actionId}/comments/${commentId}`)
}
